/**
 * pasteNormalizer (iter-49, iter-50 permissive + word) — clipboard
 * cleanup for the chapter WYSIWYG editor.
 *
 * Strict mode (default): convert style-based bold/italic/underline back
 * into semantic tags and STRIP every inline `style` attribute. Google
 * Docs and Microsoft Word wrappers are removed. What you see after
 * paste equals what you save.
 *
 * Permissive mode (iter-50): same wrapper cleanup, same semantic-tag
 * conversion, but the resulting `style` attribute is kept on text
 * tags with a strict CSS property allowlist so authors can preserve
 * colours / font sizes / alignment from Google Docs & Word.
 */
import DOMPurify from "dompurify";

// CSS properties kept in permissive mode. Must mirror the server-side
// _WYSIWYG_PERMISSIVE_CSS_PROPS in backend/server.py.
const PERMISSIVE_CSS_PROPS = new Set([
  "color", "background-color",
  "font-family", "font-size", "font-weight", "font-style",
  "text-align", "text-decoration",
  "line-height", "letter-spacing",
]);

function _semanticWraps(styleStr) {
  if (!styleStr) return [];
  const wraps = [];
  const fw = /font-weight\s*:\s*([^;]+)/i.exec(styleStr);
  if (fw) {
    const v = fw[1].trim().toLowerCase();
    const numeric = parseInt(v, 10);
    if (v === "bold" || v === "bolder"
        || (Number.isFinite(numeric) && numeric >= 600)) {
      wraps.push("strong");
    }
  }
  if (/font-style\s*:\s*italic/i.test(styleStr)) wraps.push("em");
  if (/text-decoration[^;]*underline/i.test(styleStr)) wraps.push("u");
  if (/text-decoration[^;]*line-through/i.test(styleStr)) wraps.push("s");
  if (/vertical-align\s*:\s*super/i.test(styleStr)) wraps.push("sup");
  if (/vertical-align\s*:\s*sub/i.test(styleStr)) wraps.push("sub");
  return wraps;
}

/** Keep only whitelisted CSS declarations from an inline style string. */
function _filterStyle(styleStr) {
  if (!styleStr) return "";
  const out = [];
  for (const part of styleStr.split(";")) {
    const [prop, ...rest] = part.split(":");
    if (!prop || rest.length === 0) continue;
    const name = prop.trim().toLowerCase();
    if (!PERMISSIVE_CSS_PROPS.has(name)) continue;
    const value = rest.join(":").trim();
    if (!value) continue;
    // Block javascript: / expression() / url() with javascript:
    if (/javascript:|expression\(|url\(\s*['"]?javascript:/i.test(value)) continue;
    out.push(`${name}: ${value}`);
  }
  return out.join("; ");
}

// Office namespaced tag prefixes we always drop.
const _OFFICE_NS_TAGS = new Set([
  "META", "STYLE", "SCRIPT", "XML", "O:P", "V:SHAPETYPE", "V:SHAPE",
  "V:STROKE", "V:FILL", "V:PATH", "V:FORMULAS", "V:F", "V:IMAGEDATA",
  "W:SDT", "W:SDTPR", "W:SDTCONTENT",
]);

function _isOfficeNamespaced(tag) {
  if (!tag) return false;
  const t = tag.toUpperCase();
  if (_OFFICE_NS_TAGS.has(t)) return true;
  return t.startsWith("O:") || t.startsWith("V:") || t.startsWith("W:")
      || t.startsWith("X:") || t.startsWith("M:");
}

/** Remove HTML comment nodes (Word conditional comments etc.). */
function _stripComments(root) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, 128 /* COMMENT */);
  const dead = [];
  let n = walker.nextNode();
  while (n) { dead.push(n); n = walker.nextNode(); }
  for (const c of dead) c.parentNode && c.parentNode.removeChild(c);
}

// Bullet glyph & numeric marker regexes used when converting Word
// list paragraphs (which come across the clipboard as flat <p> with
// a Symbol-font bullet character) into real <ul>/<ol><li> lists.
const _BULLET_CHARS_RE =
  /^[\s\u00a0\u2000-\u200b\u202f\u205f\u3000]*[·•◦▪▫‣⁃●○◘■□◆◇★☆*→▶►\-–—][\s\u00a0\u2000-\u200b\u202f\u205f\u3000]+/;
// A leading span in a Word list paragraph often contains ONLY the
// bullet glyph (no trailing whitespace) — accept those on their own.
const _BULLET_ONLY_RE =
  /^[\s\u00a0\u2000-\u200b\u202f\u205f\u3000]*(?:[·•◦▪▫‣⁃●○◘■□◆◇★☆*→▶►\-–—]|o)[\s\u00a0\u2000-\u200b\u202f\u205f\u3000]*$/;
const _ORDERED_MARKER_RE =
  /^[\s\u00a0]*(\d+[.)]|[a-zA-Z][.)]|[ivxlcIVXLC]+[.)])[\s\u00a0]+/;

function _looksLikeWordListP(p) {
  const cls = (p.getAttribute("class") || "").toLowerCase();
  const style = (p.getAttribute("style") || "").toLowerCase();
  if (cls.includes("msolistparagraph")) return true;
  if (/\bmso-list\s*:/.test(style)) return true;
  return false;
}

/** Strip a Symbol-font bullet marker (or numeric marker) from the
 *  start of a <p> that we've decided is a list paragraph. Removes
 *  leading <span> children that contain only bullet glyphs / tabs,
 *  then trims any leading bullet char or "1.", "a.", "iii)" style
 *  ordinal from the first text node.
 */
function _stripLeadingMarker(p) {
  // 1) Drop leading child spans that are bullet-only or whitespace.
  while (p.firstChild) {
    const c = p.firstChild;
    if (c.nodeType === 3) {
      // Text node — leave for regex strip below.
      if (!(c.nodeValue || "").trim()) { p.removeChild(c); continue; }
      break;
    }
    if (c.nodeType === 1 && c.tagName === "SPAN") {
      const t = c.textContent || "";
      if (_BULLET_ONLY_RE.test(t) || !t.trim()) { p.removeChild(c); continue; }
      const stripped = t.replace(_BULLET_CHARS_RE, "").replace(_ORDERED_MARKER_RE, "");
      if (!stripped.trim()) { p.removeChild(c); continue; }
      break;
    }
    break;
  }
  // 2) Trim the first text node of any leading bullet / ordinal /
  //    stray whitespace left by the marker's alignment tab.
  const walker = p.ownerDocument.createTreeWalker(p, 4 /* TEXT */);
  const t = walker.nextNode();
  if (t) {
    let v = t.nodeValue || "";
    v = v.replace(_BULLET_CHARS_RE, "").replace(_ORDERED_MARKER_RE, "");
    v = v.replace(/^[\s\u00a0\u2000-\u200b\u202f\u205f\u3000]+/, "");
    t.nodeValue = v;
  }
}

/** Group consecutive Word-style list <p>s into real <ul>/<ol><li>. */
function _convertWordLists(root) {
  const doc = root.ownerDocument;
  const paras = Array.from(root.querySelectorAll("p"));
  // Group runs of adjacent list paragraphs.
  const groups = [];
  let current = null;
  for (const p of paras) {
    if (!_looksLikeWordListP(p)) { current = null; continue; }
    if (current && current[current.length - 1].nextElementSibling === p) {
      current.push(p);
    } else {
      current = [p];
      groups.push(current);
    }
  }
  for (const group of groups) {
    // Decide ordered vs unordered from the first paragraph's marker.
    const firstText = group[0].textContent || "";
    const isOrdered = _ORDERED_MARKER_RE.test(firstText);
    // Level per paragraph — derived from mso-list "level<N>" (fallback 1).
    const levels = group.map((p) => {
      const style = (p.getAttribute("style") || "").toLowerCase();
      const m = /mso-list\s*:[^;]*level(\d+)/.exec(style);
      return m ? Math.max(1, parseInt(m[1], 10)) : 1;
    });
    const rootList = doc.createElement(isOrdered ? "ol" : "ul");
    const stack = [{ list: rootList, level: 1 }];
    for (let i = 0; i < group.length; i++) {
      const p = group[i];
      const lvl = levels[i];
      _stripLeadingMarker(p);
      while (stack.length > lvl) stack.pop();
      while (stack.length < lvl) {
        const parent = stack[stack.length - 1].list;
        const lastLi = parent.lastElementChild;
        const nested = doc.createElement(isOrdered ? "ol" : "ul");
        if (lastLi) lastLi.appendChild(nested);
        else parent.appendChild(nested);
        stack.push({ list: nested, level: stack.length + 1 });
      }
      const li = doc.createElement("li");
      while (p.firstChild) li.appendChild(p.firstChild);
      stack[stack.length - 1].list.appendChild(li);
    }
    // Splice the list where the first paragraph was, remove all group <p>s.
    group[0].parentNode.insertBefore(rootList, group[0]);
    for (const p of group) if (p.parentNode) p.parentNode.removeChild(p);
  }
}

/** Capitalize the first alphabetic character of every list item so
 *  bullets read as sentence-cased items even when the source doc
 *  started them lowercase.
 */
function _capitalizeListItems(root) {
  const items = root.querySelectorAll("li");
  for (const li of items) {
    const walker = li.ownerDocument.createTreeWalker(li, 4 /* TEXT */);
    let node = walker.nextNode();
    while (node) {
      const v = node.nodeValue || "";
      const idx = v.search(/\S/);
      if (idx >= 0) {
        const ch = v.charAt(idx);
        if (/[a-z]/.test(ch)) {
          node.nodeValue = v.substring(0, idx) + ch.toUpperCase() + v.substring(idx + 1);
        }
        break;
      }
      node = walker.nextNode();
    }
  }
}

function _walk(node, opts) {
  if (node.nodeType !== 1) return;

  // 1. Google Docs wrapper unwrap.
  if (node.tagName === "B" && (node.id || "").startsWith("docs-internal-guid")) {
    const parent = node.parentNode;
    const hoisted = Array.from(node.childNodes);
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    parent.removeChild(node);
    for (const c of hoisted) _walk(c, opts);
    return;
  }

  // 2. Drop Office / namespaced elements outright.
  if (_isOfficeNamespaced(node.tagName)) {
    node.parentNode && node.parentNode.removeChild(node);
    return;
  }

  // 2b. Replace <hr> horizontal rules (Google Docs "Insert horizontal
  // line", AI-generated section dividers) with a blank paragraph so
  // the vertical spacing between sections is preserved but the ugly
  // rule / underscore line is removed. Same treatment for the long
  // "____________" paragraphs some AI writers emit — handled after
  // the walk in a post-processing pass.
  if (node.tagName === "HR") {
    const p = node.ownerDocument.createElement("p");
    p.appendChild(node.ownerDocument.createElement("br"));
    node.parentNode.replaceChild(p, node);
    return;
  }

  // 3. Demote <h1> → <h2>.
  if (node.tagName === "H1") {
    const h2 = node.ownerDocument.createElement("h2");
    while (node.firstChild) h2.appendChild(node.firstChild);
    node.parentNode.replaceChild(h2, node);
    node = h2;
  }

  // 4. Recurse children first so nested styles get processed before we
  //    touch the parent.
  const children = Array.from(node.childNodes);
  for (const c of children) _walk(c, opts);

  // 5. Convert inline styles → semantic wraps.
  const style = node.getAttribute && node.getAttribute("style");
  if (style) {
    const wraps = _semanticWraps(style);
    const semanticSet = new Set(["STRONG", "EM", "U", "S", "SUP", "SUB"]);
    if (wraps.length && !semanticSet.has(node.tagName)) {
      let inner = node.ownerDocument.createDocumentFragment();
      while (node.firstChild) inner.appendChild(node.firstChild);
      let wrapper = inner;
      for (const tag of wraps) {
        const el = node.ownerDocument.createElement(tag);
        el.appendChild(wrapper);
        wrapper = el;
      }
      node.appendChild(wrapper);
    }
    if (opts.permissive) {
      const kept = _filterStyle(style);
      if (kept) node.setAttribute("style", kept);
      else node.removeAttribute("style");
    } else {
      node.removeAttribute("style");
    }
  }

  // 6. Drop attributes we never want (Word's mso-* live inside style
  //    which we already filtered; class="MsoNormal" etc. goes here).
  ["class", "id", "lang", "dir", "align", "face", "color", "size",
   "start", "type", "cellspacing", "cellpadding", "border"].forEach((a) => {
    if (node.hasAttribute && node.hasAttribute(a)) node.removeAttribute(a);
  });

  // 7. Unwrap empty <span> / <font>.
  if ((node.tagName === "SPAN" || node.tagName === "FONT")
      && node.attributes.length === 0) {
    const parent = node.parentNode;
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    parent.removeChild(node);
    return;
  }
}

/**
 * Normalize a clipboard HTML string for pasting into the chapter editor.
 * @param {string} html raw HTML from clipboardData.
 * @param {{allowedTags: string[], allowedAttrs: string[], permissive?: boolean}} whitelist
 * @returns {string} cleaned HTML ready for insertHTML.
 */
export function normalizePastedHtml(html, whitelist) {
  if (!html) return "";
  const permissive = !!whitelist.permissive;
  const doc = new DOMParser().parseFromString(
    `<div id="__pw">${html}</div>`, "text/html");
  const root = doc.getElementById("__pw");
  if (!root) return "";
  _stripComments(root);
  // Word list paragraphs come across the clipboard as flat <p>s with
  // a Symbol-font bullet glyph and a mso-list level marker. Convert
  // them into real <ul>/<ol><li> BEFORE _walk strips the class/style
  // hints we need to detect them.
  _convertWordLists(root);
  _walk(root, { permissive });
  // Sentence-case the first letter of each list item so bullets read
  // consistently regardless of how the source document capitalised
  // them.
  _capitalizeListItems(root);
  // Strip AI / Google Docs decorative separator paragraphs — lines
  // like "____________________" or "————————————" that some AI
  // writers emit between sections. Keep the vertical spacing by
  // replacing them with an empty paragraph.
  // iter-109 — Also catches lone bullet / dash spacer paragraphs
  // ("•", "◦", "-", "·", length 1-2) that Google Docs / ChatGPT
  // emit BETWEEN real list items. Previously only length>=3 ran
  // through SEPARATOR_RE so "•" survived as an orphan bullet
  // paragraph in the editor (see user screenshot — a bare `•` sat
  // between every real bulleted line).
  const SEPARATOR_RE = /^[\s_\-–—=*•·~◦▪▫‣⁃●○◘■□◆◇★☆]+$/;
  const LONE_BULLET_RE = /^[\s\u00a0]*[•·◦▪▫‣⁃●○◘■□◆◇★☆*\-–—o][\s\u00a0]*$/i;
  root.querySelectorAll("p, h1, h2, h3, h4, h5, h6, div, li").forEach((el) => {
    if (el.children.length > 0) return;
    const text = (el.textContent || "").trim();
    if (!text) return;
    const isLongSep = text.length >= 3 && SEPARATOR_RE.test(text);
    const isLoneBullet = LONE_BULLET_RE.test(text);
    if (!isLongSep && !isLoneBullet) return;
    // For <li> we just drop it — the surrounding list continues.
    if (el.tagName === "LI") {
      el.parentNode.removeChild(el);
      return;
    }
    // For block containers, replace with an empty <p> so vertical
    // rhythm is preserved only for the long decorative separators;
    // drop lone bullets entirely so no gap is left.
    if (isLoneBullet) {
      el.parentNode.removeChild(el);
    } else {
      const p = el.ownerDocument.createElement("p");
      p.appendChild(el.ownerDocument.createElement("br"));
      el.parentNode.replaceChild(p, el);
    }
  });
  // Clean up empty paragraphs / spans left behind.
  root.querySelectorAll("p, span").forEach((el) => {
    if (!el.textContent.trim() && el.children.length === 0
        && el.attributes.length === 0) {
      el.parentNode.removeChild(el);
    }
  });
  // iter-107 — Strip empty <li> items and any <ul>/<ol> that becomes
  // empty as a result. Word / Google Docs occasionally paste bullet
  // paragraphs that carry only the marker glyph (no real text) as
  // spacer rows between real bullets. Kept as-is they render as a
  // bare `•` on its own line with the actual sentence as an
  // orphaned paragraph below (author screenshot).
  root.querySelectorAll("li").forEach((li) => {
    if (!(li.textContent || "").trim() && li.querySelectorAll("img").length === 0) {
      li.parentNode.removeChild(li);
    }
  });
  root.querySelectorAll("ul, ol").forEach((list) => {
    if (list.querySelectorAll("li").length === 0) {
      list.parentNode.removeChild(list);
    }
  });
  const cleaned = root.innerHTML;
  // Final DOMPurify pass with the editor's whitelist. In permissive
  // mode we allow `style` on every whitelisted tag (DOMPurify's global
  // ALLOWED_ATTR is per-tag agnostic — a coarser but safe policy).
  const allowedAttr = permissive
    ? Array.from(new Set([...whitelist.allowedAttrs, "style"]))
    : whitelist.allowedAttrs;
  return DOMPurify.sanitize(cleaned, {
    ALLOWED_TAGS: whitelist.allowedTags,
    ALLOWED_ATTR: allowedAttr,
    // Strip any inline event handlers or javascript: URIs.
    ALLOWED_URI_REGEXP: /^(https?:|mailto:|\/|#)/i,
  });
}
